/**
 * @file LorryDrawing.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-12
 * @modified 2026-10-15
 * @description The lorry drawing — the rigid, artic and bag lorry pictures
 *              with their compartments laid over the body — shared by the
 *              load window (VehicleLoadModal: lorryArt) and the Lorry day
 *              window. The same pictures, classes and styles
 *              (VehicleModal.css, VehicleLoadModal.css under .lorry-drawing),
 *              so a lorry looks the same in both.
 *
 *              In the Lorry day window it can be edited:
 *                - a moveable body's doors are dragged to any whole tonne;
 *                - any door is clicked to open or close it (Cam: every door
 *                  opens, so one product can fill several compartments);
 *                - a compartment is clicked to put the chosen product in it,
 *                  or take it out.
 *              Each order keeps its own colour all day (ld-c0…ld-c7).
 *              Neighbouring compartments with the door between them OPEN that
 *              hold the same product (or are both empty) are drawn as one
 *              space — "Farm B 12.00 T" over C1–C2 — the open door dashed
 *              inside it; clicking the space acts on all its compartments.
 *
 *              1.1.0 (2026-10-15, Cam: "the coloured overlay for the product
 *              is too tall (as are the doors) for the diagram"): the picture
 *              keeps its own shape (aspect-ratio) and the compartments are
 *              laid over the body by where the body is IN THE PICTURE
 *              (--cargo-* as percentages of the image, per type), so they sit
 *              inside its outline at any width; the doors stay inside the
 *              body. A compartment over its size, or a lorry over its
 *              payload, is still drawn within the body and its compartment —
 *              flagged ("+2 T over", `overNote`), never drawn past it. Sizes
 *              and door positions read in whole tonnes.
 */

import React, { useRef, useState } from 'react';
import './VehicleModal.css';
import './VehicleLoadModal.css';
import { moveDoor, doorsOf } from '../utils/lorryDay';
import { formatQuantity, formatCount } from '../utils/numberFormatters';

const t2 = (n) => (Number.isFinite(Number(n)) ? formatQuantity(Number(n)) : '—');
// Compartment sizes and door positions: whole tonnes ("2T, 3T, never 2.77T").
const tw = (n) => (Number.isFinite(Number(n)) ? formatCount(Number(n)) : '—');

/** The picture for a vehicle: rigid, artic, or a bag lorry (with or without a Moffett). */
export function lorryArt(vehicle) {
  const type = vehicle?.vehicle_type;
  // `aspect` is the picture's own width / height (the PNGs in public/).
  if (type === 'articulated') return { src: '/ArticFull.png', cls: 'type-artic', aspect: '1600 / 517' };
  if (type === 'bag') {
    return vehicle?.has_moffett
      ? { src: '/BagFull_Moffett.png', cls: 'type-bag-moffett', aspect: '1600 / 596' }
      : { src: '/BagFull.png', cls: 'type-bag', aspect: '1600 / 691' };
  }
  return { src: '/LorryFull.png', cls: 'type-rigid', aspect: '1500 / 578' };
}

/**
 * @param {object}   props
 * @param {object}   props.vehicle       the vehicle spec (picture)
 * @param {boolean}  props.moveable      doors move (else fixed compartments)
 * @param {number[]} props.sizes         compartment sizes, front (cab) to back
 * @param {number[]} props.open          open doors (door k is between Ck and Ck+1)
 * @param {object[]} props.compartments  [{ n, size, items: [{ rowId, portion }], dirty }]
 * @param {Function} props.rowInfo       rowId → { name, product, colour }
 * @param {string}   [props.pickedRowId] the product being placed
 * @param {boolean}  [props.editable]
 * @param {string}   [props.editTitle]   why it cannot be edited
 * @param {Function} [props.onSizes]     new sizes (a door dragged)
 * @param {Function} [props.onToggleDoor] door number
 * @param {Function} [props.onCompartment] the compartment numbers of the space clicked
 */
function LorryDrawing({ vehicle, moveable, sizes, open = [], compartments = [], rowInfo, pickedRowId = null, editable = false, editTitle, onSizes, onToggleDoor, onCompartment, overNote = null }) {
  const art = lorryArt(vehicle);
  const overlayRef = useRef(null);
  const [drag, setDrag] = useState(null); // { door, sizes } while a door is dragged
  const shownSizes = drag ? drag.sizes : sizes;
  const length = shownSizes.reduce((s, v) => s + v, 0);
  const doors = doorsOf(shownSizes);
  const openSet = new Set(open);
  const byN = new Map(compartments.map(c => [c.n, c]));

  const tonnesAt = (clientX) => {
    const rect = overlayRef.current?.getBoundingClientRect();
    if (!rect || !rect.width) return null;
    return ((clientX - rect.left) / rect.width) * length;
  };

  const doorDown = (e, door) => {
    if (!editable) return;
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    let moved = false;
    let current = sizes;
    const onMove = (ev) => {
      if (!moveable) return;
      if (Math.abs(ev.clientX - startX) > 4) moved = true;
      if (!moved) return;
      const t = tonnesAt(ev.clientX);
      if (t == null) return;
      current = moveDoor(sizes, door, t);
      setDrag({ door, sizes: current });
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      setDrag(null);
      if (moved && moveable) {
        if (JSON.stringify(current) !== JSON.stringify(sizes) && onSizes) onSizes(current);
      } else if (onToggleDoor) {
        onToggleDoor(door);
      }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const doorKey = (e, door) => {
    if (!editable) return;
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (onToggleDoor) onToggleDoor(door); return; }
    if (moveable && (e.key === 'ArrowLeft' || e.key === 'ArrowRight') && onSizes) {
      e.preventDefault();
      onSizes(moveDoor(sizes, door, doors[door - 1] + (e.key === 'ArrowLeft' ? -1 : 1)));
    }
  };

  // Compartments joined by an open door, holding the same product (or both
  // empty), are one space: [{ nums: [1, 2], size }].
  const sameLoad = (a, b) => {
    const ia = (byN.get(a)?.items || []).map(i => String(i.rowId)).sort().join(',');
    const ib = (byN.get(b)?.items || []).map(i => String(i.rowId)).sort().join(',');
    return ia === ib;
  };
  const spaces = [];
  shownSizes.forEach((size, i) => {
    const n = i + 1;
    const last = spaces[spaces.length - 1];
    if (last && openSet.has(n - 1) && sameLoad(n - 1, n)) { last.nums.push(n); last.size += size; }
    else spaces.push({ nums: [n], size });
  });

  const segment = (nums, size, style, fixed) => {
    const n = nums[0];
    const label = nums.length > 1 ? `C${nums[0]}–C${nums[nums.length - 1]}` : `C${n}`;
    const c = { items: nums.flatMap(k => (byN.get(k)?.items || [])), dirty: nums.some(k => byN.get(k)?.dirty) };
    // More in it than it holds: drawn full, within its bounds, and flagged.
    const overBy = nums.reduce((sum, k) => sum + (Number(byN.get(k)?.over) || 0), 0);
    const merged = new Map();
    for (const it of c.items) merged.set(String(it.rowId), { rowId: it.rowId, portion: (merged.get(String(it.rowId))?.portion || 0) + it.portion });
    const items = [...merged.values()];
    const portion = items.reduce((s, i) => s + i.portion, 0);
    const fillPct = size > 0 ? Math.min((portion / size) * 100, 100) : 0;
    const first = items[0] ? rowInfo(items[0].rowId) : null;
    const many = items.length > 1;
    const picked = pickedRowId != null && items.some(i => String(i.rowId) === String(pickedRowId));
    const cls = fixed
      ? `vm-comp vl-comp ${first ? 'vm-comp-filled' : 'vl-comp-empty'}`
      : `vm-door-seg vlm-seg ${first ? '' : 'vlm-empty'}`;
    const title = first
      ? `${label}: ${items.map(i => { const r = rowInfo(i.rowId); return `${r.name} · ${r.product} ${t2(i.portion)} T`; }).join(' + ')} in ${tw(size)} T${overBy > 0.0005 ? ` — ${t2(overBy)} T more than it holds` : ''}`
      : `${label}: empty, ${tw(size)} T`;
    const clickable = editable && onCompartment;
    return (
      <div
        key={`c-${nums.join('-')}`}
        className={`${cls}${many ? ' vlm-mixed' : ''}${picked ? ' ld-picked' : ''}${clickable ? ' ld-clickable' : ''}${c.dirty ? ' ld-dirty' : ''}${overBy > 0.0005 ? ' ld-over' : ''}`}
        style={style}
        title={clickable ? `${title}${pickedRowId != null ? ' — click to put the chosen product in, or take it out' : ''}` : (editable ? title : `${title}${editTitle ? ` — ${editTitle}` : ''}`)}
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        onClick={clickable ? () => onCompartment(nums) : undefined}
        onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onCompartment(nums); } } : undefined}
      >
        <div className={`vlm-fill${first ? ` ld-c${first.colour}` : ''}`} style={{ height: `${first ? fillPct : 0}%` }} />
        <span className={fixed ? 'vm-comp-index' : 'vm-door-seg-index'}>{label}</span>
        {first ? (
          <>
            <span className="vl-seg-order" title={first.name}>{first.name}{many ? ` +${items.length - 1}` : ''}</span>
            <span className="ld-seg-product">{first.product}</span>
            <span className={fixed ? 'vm-comp-value' : 'vm-door-seg-value'}>
              <span className="vm-door-seg-num">{t2(portion)}</span>
              <span className="vm-comp-unit">T</span>
              <span className="vlm-of">/{tw(size)}T</span>
            </span>
          </>
        ) : (
          <span className="vlm-empty-cap"><em>empty</em><br />{tw(size)}<span> T</span></span>
        )}
        {c.dirty && <span className="ld-sweep" title="Carried medicated feed — sweep out before it is refilled">sweep</span>}
        {overBy > 0.0005 && <span className="ld-over-tag" title={`${t2(overBy)} T more than ${label} holds`}>+{t2(overBy)} T</span>}
      </div>
    );
  };

  const door = (k, leftPct) => {
    const isOpen = openSet.has(k);
    const pos = doors[k - 1];
    return (
      <div
        key={`d-${k}`}
        className={`vlm-door ld-door${isOpen ? ' ld-door-open' : ''}${editable ? ' ld-door-edit' : ''}${drag && drag.door === k ? ' ld-door-drag' : ''}`}
        style={{ left: `${leftPct}%` }}
        role={editable ? 'button' : undefined}
        tabIndex={editable ? 0 : undefined}
        aria-label={`Door ${k}, between C${k} and C${k + 1}${moveable ? ` at ${tw(pos)} T` : ''}, ${isOpen ? 'open' : 'closed'}`}
        title={editable
          ? `${moveable ? 'Drag to move this door (whole tonnes); ' : ''}click to ${isOpen ? 'close' : 'open'} it${moveable ? ' — ← → move it by 1 T' : ''}`
          : (editTitle || `Door ${isOpen ? 'open' : 'closed'}`)}
        onPointerDown={(e) => doorDown(e, k)}
        onKeyDown={(e) => doorKey(e, k)}
      >
        <span className="vlm-door-label">{isOpen ? 'open' : (moveable ? `${tw(pos)}T` : '')}</span>
      </div>
    );
  };

  let overlay;
  if (!shownSizes.length) {
    overlay = <div className="vm-compartments-empty">No compartment layout for this vehicle.</div>;
  } else if (moveable) {
    let cum = 0;
    overlay = (
      <div className="vm-doors-overlay vlm-overlay" ref={overlayRef}>
        {spaces.map((sp) => {
          const left = cum; cum += sp.size;
          return segment(sp.nums, sp.size, { left: `${(left / length) * 100}%`, width: `${(sp.size / length) * 100}%` }, false);
        })}
        {doors.map((pos, i) => door(i + 1, (pos / length) * 100))}
      </div>
    );
  } else {
    overlay = (
      <div className="vm-compartments ld-fixed" ref={overlayRef}>
        {spaces.map((sp) => segment(sp.nums, sp.size, { flex: `${sp.nums.length} 1 0` }, true))}
        {doors.map((_, i) => door(i + 1, ((i + 1) / shownSizes.length) * 100))}
      </div>
    );
  }

  // The picture at its own shape; the overlay placed by where the body is in
  // the picture (LorryDayWindow.css, .ld-art.type-*), so it cannot spill.
  return (
    <div className="lorry-drawing">
      <div className={`ld-art ${art.cls}${overNote ? ' ld-art-over' : ''}`} style={{ aspectRatio: art.aspect }}>
        <img className="ld-art-img" src={art.src} alt={vehicle?.registration || 'Lorry'} />
        {overlay}
        {overNote && <span className="ld-art-flag" title={overNote}><span className="material-symbols-rounded">scale</span>{overNote}</span>}
      </div>
    </div>
  );
}

export default LorryDrawing;
